/**
 * usePermissions — centralized RBAC permission checks for the frontend.
 * Reads the user's role from the auth context and exposes boolean helpers.
 * When impersonating (superadmin only), derives permissions from the
 * impersonated role — but isSuperAdmin always reflects the ACTUAL role.
 *
 * The pure permission model lives in auth/rolePermissions.ts (kept in
 * lockstep with the backend by rbac-contract.test.ts).
 */
import { useMemo } from 'react';
import { useAuth } from '../auth/useAuth';
import { useImpersonation } from './useImpersonation';
import { derivePermissions, type AppRole, type RolePermissionFlags } from '../auth/rolePermissions';

export type { AppRole };

export interface Permissions extends RolePermissionFlags {
  /** The user's real role (never changes during impersonation) */
  actualRole: AppRole;
  /** Always reflects the ACTUAL role, never the impersonated one */
  isSuperAdmin: boolean;
}

export function usePermissions(): Permissions {
  const { user } = useAuth();
  const { impersonatingAs } = useImpersonation();

  const actualRole = ((user as any)?.role || 'reader') as AppRole;
  const effectiveRole = (actualRole === 'superadmin' && impersonatingAs) ? impersonatingAs : actualRole;

  return useMemo(() => ({
    ...derivePermissions(effectiveRole),
    actualRole,
    isSuperAdmin: actualRole === 'superadmin',
  }), [effectiveRole, actualRole]);
}
